/**
 * Deterministic color assignment for collaborator cursors / avatars /
 * selection halos. Same uid always renders in the same color, so a
 * collaborator looks consistent across reloads and across views.
 *
 * The palette is hand-picked for: high contrast against the canvas's
 * neutral backgrounds; no two adjacent colors that look similar; readable
 * on both light and dark UI surfaces.
 *
 * Hex strings are kept upper-case so they round-trip cleanly through
 * Firestore/RTDB without case-folding surprises.
 */

const PALETTE = [
  "#2A70F5", // accent blue
  "#E0467A", // pink
  "#7C3AED", // violet
  "#F59E0B", // amber
  "#10B981", // emerald
  "#EF4444", // red
  "#06B6D4", // cyan
  "#8B5CF6", // purple
  "#F472B6", // pink-400
  "#84CC16", // lime
  "#0EA5E9", // sky
  "#D946EF", // fuchsia
] as const;

/** Map a uid to a stable color from the palette. */
export function colorForUid(uid: string): string {
  let h = 0;
  for (let i = 0; i < uid.length; i++) {
    h = (h * 31 + uid.charCodeAt(i)) | 0;
  }
  // Force unsigned then index.
  const idx = Math.abs(h) % PALETTE.length;
  return PALETTE[idx];
}

/** Extract initials for an avatar fallback when there's no photoURL. */
export function initialsFromName(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts
    .map((p) => p[0]?.toUpperCase() ?? "")
    .filter(Boolean)
    .join("") || "?";
}
